import { Button, CCInteger, _decorator } from 'cc';
import { DEV } from 'cc/env';
import Delay from '../../Utils/Delay';
import ValueSelector from '../../Helper/ValueSelector';
import ValuePicker from './ValuePicker';

const { ccclass, property } = _decorator;

enum EventType {
    NEXT_VALUE = 'NEXT_VALUE',
    PREVIOUS_VALUE = 'PREVIOUS_VALUE',
}

@ccclass('OptionValuePicker')
export default class OptionValuePicker extends ValuePicker {
    public static readonly EVENT_TYPE = { ...EventType, ...ValuePicker.EVENT_TYPE };

    @property(Button)
    public readonly maxButton: Button = null;

    @property(Button)
    public readonly minButton: Button = null;

    @property({
        tooltip: DEV && '是否禁用無效按鈕（當前值已經是最大或最小值時）',
        visible(this: OptionValuePicker) {
            return !this.hideNoEffectButton;
        },
    })
    protected readonly disableNoEffectButton = false;

    @property({
        tooltip: DEV && '是否隱藏無效按鈕（當前值已經是最大或最小值時）',
        visible(this: OptionValuePicker) {
            return !this.disableNoEffectButton;
        },
    })
    protected readonly hideNoEffectButton = false;

    @property([CCInteger])
    protected readonly valueOptions: number[] = [];

    @property
    protected readonly defaultIndex: number = 0;

    @property
    protected readonly cycleSelectionFromTail = false;

    @property
    protected readonly cycleSelectionFromHead = false;

    @property({
        tooltip: DEV && '每步之間的延遲時間',
        visible(this: OptionValuePicker) {
            return this.enableRunning;
        },
    })
    protected readonly stepDelay = 1000;

    @property({
        tooltip: DEV && '每次變化的步長',
        visible(this: OptionValuePicker) {
            return this.enableRunning;
        },
    })
    protected readonly stepSize = 1;

    protected valueSelector: ValueSelector<number> = new ValueSelector([]);

    public get value() {
        return this.valueSelector.value;
    }
    public set value(value: number) {
        this.valueSelector.value = value;
    }

    public get index() {
        return this.valueSelector.currentIdx;
    }
    public set index(index: number) {
        this.valueSelector.currentIdx = index;
    }

    public onLoad() {
        super.onLoad();
        this.maxButton?.node.on(Button.EventType.CLICK, () => this.valueSelector.lastValue());
        this.minButton?.node.on(Button.EventType.CLICK, () => this.valueSelector.firstValue());
    }

    public start(): void {
        this.updateInteractable();
    }

    public init(valueSelector?: ValueSelector<number>) {
        if (valueSelector) {
            this.valueSelector = valueSelector;
        } else {
            this.valueSelector = new ValueSelector<number>(this.valueOptions);
            this.valueSelector.currentIdx = this.defaultIndex;
        }
        this.valueSelector.cycleSelectionFromTail = this.cycleSelectionFromTail;
        this.valueSelector.cycleSelectionFromHead = this.cycleSelectionFromHead;
        // 監聽 valueSelector，當數值改變時也要改變顯示的文字
        this.valueSelector.on(ValueSelector.EVENT_TYPE.VALUE_CHANGED, this.onValueChanged, this);
        this.valueSelector.on(ValueSelector.EVENT_TYPE.OPTIONS_UPDATED, this.onValueOptionsUpdated, this);
        this.onValueChanged();
    }

    public deinit() {
        if (!this.valueSelector) {
            return;
        }
        this.valueSelector.off(ValueSelector.EVENT_TYPE.VALUE_CHANGED, this.onValueChanged, this);
        this.valueSelector.off(ValueSelector.EVENT_TYPE.OPTIONS_UPDATED, this.onValueOptionsUpdated, this);
        this.valueSelector = null;
    }

    protected *run(isUpward: boolean) {
        this.stepOnce(isUpward, false);
        while (true) {
            yield Delay.resolve(this.stepDelay);
            if (this.isPaused) {
                continue;
            }
            for (let i = 0; i < this.stepSize; i++) {
                this.stepOnce(isUpward, false);
            }
        }
    }

    protected stepOnce(isUpward: boolean, isClick: boolean): void {
        if (isUpward) {
            this.valueSelector.cycleSelectionFromTail = isClick && this.cycleSelectionFromTail;
        } else {
            this.valueSelector.cycleSelectionFromHead = isClick && this.cycleSelectionFromHead;
        }
        if (isUpward) {
            this.valueSelector.nextValue();
            this.emit(EventType.NEXT_VALUE, this.valueSelector.value);
        } else {
            this.valueSelector.previousValue();
            this.emit(EventType.PREVIOUS_VALUE, this.valueSelector.value);
        }
    }

    protected onValueChanged() {
        if (this.valueNumber && !this.valueSelector.isNullValue) {
            this.valueNumber.value = this.valueSelector.value;
        }
        this.updateInteractable();
        this.emit(OptionValuePicker.EVENT_TYPE.VALUE_CHANGED, this.valueSelector.value);
    }

    protected onValueOptionsUpdated() {
        this.updateInteractable();
        if (this.valueNumber && !this.valueSelector.isNullValue) {
            this.valueNumber.value = this.valueSelector.value;
        }
    }

    protected updateInteractable() {
        super.updateInteractable();
        this.updateButtonState(this.maxButton, !this.interactable || this.valueSelector?.isLastValue);
        this.updateButtonState(this.minButton, !this.interactable || this.valueSelector?.isFirstValue);
        this.updateButtonState(this.increaseButton, !this.interactable || (this.valueSelector?.isLastValue && !this.cycleSelectionFromTail));
        this.updateButtonState(this.decreaseButton, !this.interactable || (this.valueSelector?.isFirstValue && !this.cycleSelectionFromHead));
    }

    protected updateButtonState(button: Button, isDisabled: boolean) {
        if (!button) {
            return;
        }
        if (this.hideNoEffectButton) {
            button.node.active = !isDisabled;
        } else if (this.disableNoEffectButton) {
            button.interactable = this.interactable && !isDisabled;
        }
    }
}
